import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';

// API/Components
import axiosInstance from '../components/axios';
import useLocationGraphConfig from '../components/hooks/useLocationGraphConfig';
import useHotkeyHandlers, { HOTKEY_NAV } from '../components/hooks/useHotkeyHandlers';
import DataViewSwitch from '../components/data_view_switch';
import UnitDischargeTrendReport from '../components/unit_discharge_trend_report';
import UnitDischargeTrendGraph from '../components/unit_discharge_trend_graph';
import { parseDateTime, formatDateTime } from '../components/enums';

// Material UI
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';


// System Trend sub-page rendering (Location Name, Title)
export default function UnitDischargeTrend() {
	const { locationNum, eventDateTime } = useParams();
	const [ searchParams ] = useSearchParams();

	// State Management
	const [selectedString, setSelectedString] = useState(1);
	const [selectedUnit, setSelectedUnit] = useState(1);

	const [trendData, setTrendData] = useState({});
	const { locationInfo, limitData, graphScalesData, isLoading, setIsLoading } = useLocationGraphConfig({
		locationNum,
		limitsEndpoint: `/loc/${locationNum}/limits/discharge/unit`,
		scalesEndpoint: `/loc/${locationNum}/graph_scales/unit`
	});
	
	const [graphDisplay, setGraphDisplay] = useState(false);
	const [graphType, setGraphType] = useState(0);
	
	const numStringsRef = useRef();
	const numUnitsRef = useRef();
	const selectedStringRef = useRef();
	const selectedUnitRef = useRef();
	const graphTypeRef = useRef();
	const eventDateObj = parseDateTime(eventDateTime);

	// Helpers to check if trendData is empty and if DataViewSwitch should be disabled
	const isTrendDataEmpty = !trendData || (Object.keys(trendData).length === 0);
	const isDataSwitchDisabled = isLoading || isTrendDataEmpty;
	const dataSwitchDisabledText = isTrendDataEmpty ? "No data available to graph" : "Data is loading...";

	// Handle the current numStrings and numUnits for hotkey nav
	useEffect(() => {
		numStringsRef.current = locationInfo.num_strings;
		numUnitsRef.current = locationInfo.num_units_per_string;
	}, [locationInfo]);

	// Handle the current selectedString for hotkey nav
	useEffect(() => {
		selectedStringRef.current = selectedString;
	}, [selectedString]);

	// Handle the current selectedUnit for hotkey nav
	useEffect(() => {
		selectedUnitRef.current = selectedUnit;
	}, [selectedUnit]);

	// Handle the current graphType for hotkey nav
	useEffect(() => {
		graphTypeRef.current = graphType;
	}, [graphType]);

	// Handle keypresses for unit and graph navigation
	useHotkeyHandlers([
		{
			key: 'PageUp',
			down: (e) => {
				e.preventDefault();  // Prevent default scrolling behavior
				handleUnitNavigation(HOTKEY_NAV.BACK);
			},
			up: () => {},
		},
		{
			key: 'PageDown',
			down: (e) => {
				e.preventDefault();
				handleUnitNavigation(HOTKEY_NAV.FORWARD);
			},
			up: () => {},
		},
		{
			key: 'F8',
			down: (e) => {
				e.preventDefault();
				switch(graphTypeRef.current) {
					case 0:  // Voltage -> Temperature
						setGraphType(1);
						break;
					case 1:  // Temperature -> Voltage
						setGraphType(0);
						break;
					default:
						setGraphType(0);
						break;
				}
			},
			up: () => {},
		},
	]);

	// Handle unit navigation to allow for unit wrapping when hitting the beginning/end of a string
	function handleUnitNavigation(unitNav) {
		const numStrings = numStringsRef.current;
		const numUnits = numUnitsRef.current;

		const stringNum = selectedStringRef.current;
		const unitNum = selectedUnitRef.current;

		if (selectedStringRef.current > 0 && selectedUnitRef.current > 0) {
			if (unitNav === HOTKEY_NAV.BACK) {
				if (unitNum > 1) {  // Decrement unit
					setSelectedUnit(Number(unitNum - 1));
				} else if (unitNum === 1 && stringNum > 1) {  // Wrap selected unit to last unit of previous string
					setSelectedString(Number(stringNum - 1));
					setSelectedUnit(numUnits);
				}
			}

			if (unitNav === HOTKEY_NAV.FORWARD) {
				if (unitNum < numUnits) {  // Increment unit
					setSelectedUnit(Number(unitNum + 1));
				} else if (unitNum === numUnits && stringNum < numStrings) {  // Wrap selected unit to first unit of next string
					setSelectedString(Number(stringNum + 1));
					setSelectedUnit(1);
				}
			}
		}
	}
	
	// Get information about the location id specified (name, size, num of temps)
	useEffect(() => {
		setSelectedString(Number(searchParams.get("string")) || 1);
		setSelectedUnit(Number(searchParams.get("unit")) || 1);
	}, [locationNum, searchParams]);
	
	// Get the list of trend data for the specified location id, string number, and unit number
	useEffect(() => {
		if (!selectedString || !selectedUnit) return;

		setIsLoading(true);

		// Get Unit Trend Data
		axiosInstance.get(`/loc/${locationNum}/discharges/${eventDateTime}/string_${selectedString}/unit_${selectedUnit}`)
			.then(response => {
				setTrendData(response.data);
			})
			.catch(error => {
				console.error("Error fetching discharge trend data:", error);
			})
			.finally(() => {
				setIsLoading(false);
			});
	}, [locationNum, eventDateTime, selectedString, selectedUnit]);

	// Event Handlers
	const handleGraphTypeChange = (event) => {
		setGraphType(Number(event.target.value));
	}

	const handleStringChange = (event) => {
		setSelectedString(Number(event.target.value));
	}
	
	const handleUnitChange = (event) => {
		setSelectedUnit(Number(event.target.value));
	}
	
	const handleViewChange = (event) => {
		setGraphDisplay(event.target.checked);
	}
		
	return (
		<Paper
			padding='true'
			sx={{
				overflow: 'auto',
				boxShadow: '0',
				height: '100%',
				display: 'flex',
				flexDirection: 'column',
			}}
		>
			<Typography
				variant='h5'
				fontFamily='Saira, sans-serif'
				fontWeight='600'
				color='#0F1820'
				align='center'
				padding='5px'
			>
				{ locationInfo.location_name } (SN {locationInfo.serial_number})
			</Typography>
			<Toolbar>
				<Grid container spacing={1} alignItems="center">
					<Grid item xs alignItems="left">
						<Typography
							align="left"
							fontFamily='Saira,sans-serif'
							fontWeight='600'
							sx={{ flexGrow: 1}}
						>
							String {selectedString} Unit {selectedUnit} Discharge Trend:
						</Typography>
						<Typography
							align="left"
							fontFamily='Saira,sans-serif'
							fontWeight='600'
							sx={{ flexGrow: 1}}
						>
							{formatDateTime(eventDateObj)}
						</Typography>
					</Grid>
					{graphDisplay &&
						<Grid item>
							<FormControl sx={{ m: 1, minWidth: 100 }}>
								<label id="graph-type-select" style={{fontFamily:"Saira, sans-serif"}}>Graph Type</label>
								<select
									id="graph-type-select"
									value={graphType}
									label="Graph Type"
									onChange={handleGraphTypeChange}
								>
									<option key={0} value={0}>Voltage</option>
									<option key={1} value={1}>Temperature</option>
								</select>
							</FormControl>
						</Grid>
					}
					<Grid item>
						<FormControl sx={{ m: 0, minWidth: 80 }}>
							<label id="string-select" style={{fontFamily:"Saira, sans-serif"}}>String</label>
							<select
								id="string-select"
								value={selectedString}
								label="String"
								onChange={handleStringChange}
								sx= {{ maxHeight: 500 }}
							>
								{ Array.from({length: locationInfo.num_strings}, (_, i) => i + 1).map((stringNum) => (
									<option key={stringNum} value={stringNum}>String {stringNum}</option>
								)) }
							</select>
						</FormControl>
					</Grid>
					<Grid item>
						<FormControl sx={{ m: 0, minWidth: 80 }}>
							<label id="unit-select" style={{fontFamily:"Saira, sans-serif"}}>Unit</label>
							<select
								id="unit-select"
								value={selectedUnit}
								label="Unit"
								onChange={handleUnitChange}
								sx= {{ maxHeight: 500 }}
							>
								{ Array.from({length: locationInfo.num_units_per_string}, (_, i) => i + 1).map((unitNum) => (
									<option key={unitNum} value={unitNum}>Unit {unitNum}</option>
								)) }
							</select>
						</FormControl>
					</Grid>
					<Grid item>
						<DataViewSwitch
							checked={graphDisplay}
							onChange={handleViewChange}
							leftText="Report"
							rightText="Graph"
							disabled={isDataSwitchDisabled}
							disabledText={dataSwitchDisabledText}
						/>
					</Grid>
				</Grid>
			</Toolbar>
			{ graphDisplay ?
				<UnitDischargeTrendGraph
					trendData={trendData}
					limitData={limitData}
					graphScalesData={graphScalesData}
					selectedString={selectedString}
					selectedUnit={selectedUnit}
					graphType={graphType}
					loading={isLoading}
				/>
			:
				<UnitDischargeTrendReport
					trendData={trendData}
					loading={isLoading}
				/>
			}
		</Paper>
	);
}